/**
 * La route "pertes" ({ idExploitation, rcp, annee }) renvoie TOUT en une
 * seule réponse : il n'y a pas 3 routes séparées (pertes / aléas-par-culture /
 * répartition-cultures), contrairement à ce qui avait été codé au départ.
 * Réponse réelle observée :
 *
 *   {
 *     aleasParCulture:    { "2020": [...], "2035": [...], "2050": [...] },
 *     repartitionCultures:{ "2020": [...], "2035": [...], "2050": [...] },
 *     repartitionAleas:   [ { YearGroup, alea, val }, ... ]  // toutes années à plat
 *   }
 *
 * Ces fonctions sont les seules à savoir lire cette forme ; les composants
 * (ImpactsGlobaux, PertesTab...) ne parlent plus qu'à elles, jamais
 * directement aux champs de la réponse brute.
 */

/**
 * Clé d'année utilisée dans aleasParCulture/repartitionCultures.
 * ⚠️ Seules les clés RCP classiques ("2020"/"2035"/"2050") sont confirmées
 * par l'API à ce jour. Pour TRACC, la clé "+2"/"+2.7"/"reference" est
 * supposée par cohérence avec repartitionAleas (YearGroup) — à confirmer
 * avec Clément dès que l'API répond réellement en TRACC sur cette route.
 */
function cleAnneePertes(rcp, annee) {
  if (rcp !== "tracc") return String(Number(annee));
  return annee === "reference" ? "reference" : `+${annee}`;
}

/** Répartition du risque par culture pour l'horizon choisi (camembert). */
export function repartitionCulturesPourAnnee(donnees, rcp, annee) {
  return donnees?.repartitionCultures?.[cleAnneePertes(rcp, annee)] ?? [];
}

/** Pertes par culture et par aléa pour l'horizon choisi (barres empilées). */
export function aleasParCulturePourAnnee(donnees, rcp, annee) {
  return donnees?.aleasParCulture?.[cleAnneePertes(rcp, annee)] ?? [];
}

/**
 * Reconstitue la forme { total, repartition } attendue par PertesChart à
 * partir de `repartitionAleas` (toutes années à plat) :
 *  - repartition : directement repartitionAleas, même forme qu'avant ;
 *  - total       : pas renvoyé par l'API, donc calculé ici comme la somme des
 *    5 aléas par année (seule donnée disponible pour tracer la courbe/sizer
 *    les camemberts).
 */
export function totalEtRepartitionAleas(donnees) {
  const repartition = donnees?.repartitionAleas ?? [];

  const sommeParAnnee = new Map();
  repartition.forEach(({ YearGroup, val }) => {
    sommeParAnnee.set(YearGroup, (sommeParAnnee.get(YearGroup) ?? 0) + val);
  });
  const total = Array.from(sommeParAnnee, ([YearGroup, val]) => ({
    YearGroup,
    val: Number(val.toFixed(1)),
  }));

  return { total, repartition };
}
